import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import styled from "styled-components";
import "../Articles/content-styles.css";
import {
  autoCapitaliseFirstLetter,
  scrollToIdOrClass,
} from "../../Constants/utility";
import { useArticleTracking } from "../../hooks/useArticleTracking";
import { useSlugTitles } from "../../hooks/useSlugTitles";
import { BreadCrumbs } from "../BreadCrumbs";

const PreviewArticleContainer = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  padding-left: 100px;
  box-sizing: border-box;

  @media screen and (max-width: 1200px) {
    padding-left: 80px;
  }

  @media screen and (max-width: 1100px) {
    padding-left: 50px;
  }

  @media screen and (max-width: 778px) {
    padding-left: 0px;
  }

  transition: padding 1s;
`;

const ArticleBody = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-family: Inter;
`;

const ArticlePrevWrap = styled.div`
  display: block;
  width: 100%;
`;

export default function LegacyPreviewArticleComp({ selectedArticle }) {
  const originalDocTitle = useRef("");

  useArticleTracking(selectedArticle);

  const slugs = selectedArticle?.fullPath?.split('/') ?? [];
  const categorySlugs = slugs.slice(0, -1);
  const { slugTitleMap, isLoading: isSlugTitlesLoading } = useSlugTitles(categorySlugs);

  //  maintain the order of useEffect
  useEffect(() => {
    setTimeout(() => scrollToIdOrClass(globalThis.location.hash), 1500);
    originalDocTitle.current = document.title;

    return () => {
      document.title = originalDocTitle.current;
    };
  }, []);

  useEffect(() => {
    articleDiv();
    const docTitle = selectedArticle?.title;
    if (!docTitle || document.title) return;
    else {
      document.title = autoCapitaliseFirstLetter(docTitle);
    }
  });

  useEffect(() => {
    window.addEventListener("resize", articleDiv);
    return () => {
      window.removeEventListener("resize", articleDiv);
    };
  }, [selectedArticle]);

  const articleDiv = () => {
    const div = document.getElementById("ck");
    if (div) {
      const containerWidth = div.offsetWidth;
      setImageSize(div, containerWidth);
    }
  };

  const setImageSize = (div, containerWidth) => {
    let imgArray = div.querySelectorAll("img");
    imgArray.forEach((image) => {
      if (image.className === "") {
        image.style.width = containerWidth + "px";
      } else if (image.style.width !== "100%") {
        let imageWidth = image?.style?.width?.replace("px", "");
        if (Number.parseFloat(imageWidth) > containerWidth) {
          image.style.width = containerWidth + "px";
        } else {
          image.style.width = imageWidth + "px";
        }
      }
    });
  };

  function stripHtml(html) {
    let tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    let figureArray = tmp.querySelectorAll("figure");
    figureArray.forEach((figure) => {
      let oembed = figure?.querySelector("oembed");
      figure.style.width = "auto";
      figure.style.height = "auto";
      if (oembed) {
        let url = oembed?.getAttribute("url");
        let videoId = getVedioId(url);
        let ifrm = document.createElement("iframe");
        if (videoId) {
          ifrm?.setAttribute("src", `//www.youtube.com/embed/${videoId}`);
        }
        figure.replaceChildren(ifrm);
      }
    });
    return tmp.innerHTML;
  }

  const getVedioId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match?.[2]?.length === 11) {
      return match[2];
    } else {
      return false;
    }
  };

  const sanitizedTitle = DOMPurify.sanitize(selectedArticle?.title, {
    ADD_TAGS: ["oembed", "iframe"],
    ADD_ATTR: ["url", "src"],
  });
  const sanitizedContent = DOMPurify.sanitize(selectedArticle?.content, {
    ADD_TAGS: ["oembed", "iframe"],
    ADD_ATTR: ["url", "src"],
  });

  const slugNameMap = {
    ...slugTitleMap,
    ...(selectedArticle?.slug ? { [selectedArticle?.slug]: selectedArticle?.title } : null),
  };

  return (
    <PreviewArticleContainer>
      <BreadCrumbs fullPath={selectedArticle?.fullPath} isLoading={isSlugTitlesLoading} slugNameMap={slugNameMap} />
      {selectedArticle && Object.keys(selectedArticle).length > 0 ? (
        <ArticleBody id="ck" className="ck-content">
          <h1 dangerouslySetInnerHTML={{ __html: sanitizedTitle }} />
          <ArticlePrevWrap
            dangerouslySetInnerHTML={{ __html: stripHtml(sanitizedContent) }}
          />
        </ArticleBody>
      ) : null}
    </PreviewArticleContainer>
  );
}
